import React from "react";
import {
  Box,
  IconButton,
  Link,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { endOfYear, format, startOfYear } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import InfoIcon from "@icons/myicons/warning.svg?react";
import { useTaxEstimate } from "@features/cashback/components/CashbackSummary/TaxHelper/useTaxEstimate";
import { TaxationSummary } from "@features/cashback/components/CashbackSummary/TaxHelper/TaxationSummary";
import { LoadingPlaceHolder } from "@rentablo/components";
import { Banner } from "@rentablo/components";
import { TaxationProducts } from "@features/cashback/components/CashbackSummary/TaxHelper/TaxationProducts";

type Props = {
  accountHolderId: number;
  date?: string;
  onBackClick: () => void;
};

export const TaxHelper = ({ accountHolderId, date, onBackClick }: Props) => {
  const { t } = useTranslation();
  const isLessMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const startDate = date
    ? startOfYear(new Date(date))
    : startOfYear(new Date());
  const endDate = date ? endOfYear(new Date(date)) : endOfYear(new Date());

  const { data, isFetching, isFetchedAfterMount } = useTaxEstimate(
    {
      accountHolderId,
      estimateFrom: format(startDate, "yyyy-MM-dd"),
      estimateTo: format(endDate, "yyyy-MM-dd"),
    },
    {
      enabled: Boolean(accountHolderId),
    }
  );

  return (
    <Box maxWidth={1200} pb={isLessMd ? 10 : 30}>
      <Box display="flex" alignItems="center" gap={3} mb={6}>
        <IconButton
          color="tertiary"
          onClick={onBackClick}
          sx={{
            "& svg": {
              width: "20px",
              height: "20px",
            },
          }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <Typography variant="h6">
          {t("domains.cashback.dashboard.taxHelper.title")}
        </Typography>
      </Box>
      {isFetching && !isFetchedAfterMount ? (
        <LoadingPlaceHolder />
      ) : (
        <Box>
          {Boolean(data?.taxationEstimates?.length) && (
            <TaxationSummary data={data} />
          )}
          <Banner
            type="info"
            mb={6}
            icon={<InfoIcon />}
            title={t("domains.cashback.dashboard.taxHelper.banner.title")}
            text={t("domains.cashback.dashboard.taxHelper.banner.text")}
          />
          <Box mb={6}>
            <Typography variant="body1" mb={2} fontWeight={600}>
              {t("domains.cashback.dashboard.taxHelper.info.title")}
            </Typography>
            <Typography variant="body1">
              <Trans
                i18nKey="domains.cashback.dashboard.taxHelper.info.text"
                components={{
                  linkText: (
                    <Link
                      href="https://www.wmdaten.de/index.php"
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  ),
                }}
              />
            </Typography>
            <Typography variant="body1">
              {t("domains.cashback.dashboard.taxHelper.info.text2")}
            </Typography>
          </Box>
          <TaxationProducts data={data} />
        </Box>
      )}
    </Box>
  );
};
